import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, ConfirmationDialog} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from './constants/labels'
// RELAY-CLEANUP: Remove commitDeleteMilestoneMutation once issues_milestone_delete_tanstack is fully rolled out
import {commitDeleteMilestoneMutation} from './mutations/delete-milestone-mutation'
// RELAY-CLEANUP: Remove commitUpdateMilestoneMutation once issues_milestone_toggle_tanstack is fully rolled out
import {commitUpdateMilestoneMutation} from './mutations/update-milestone-mutation'
import {useDeleteMilestoneMutation} from './mutations/use-delete-milestone'
import type {MilestoneNode} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'
import {useToggleMilestoneStateMutation} from './use-toggle-milestone-state'

// RELAY-CLEANUP(RepositoryMilestoneIndexPageContentInternal): widens nullable fields so the still-Relay parent
// data is assignable. Remove when RepositoryMilestoneIndexPageContentInternal leaves Relay.
export type MilestoneRowMenuFragment = RelayCompat<Pick<MilestoneNode, 'id' | 'state' | 'url' | 'repository'>>

type MilestoneRowMenuProps = {
  milestone: MilestoneRowMenuFragment
}

/**
 * Extracts the milestone number from a milestone URL.
 * URL format: github.com/owner/repo/milestone/3 or https://github.com/owner/repo/milestone/3
 */
function extractMilestoneNumber(url: string): number {
  const parts = url.split('/')
  const milestoneIndex = parts.lastIndexOf('milestone')
  return Number(parts[milestoneIndex + 1])
}

/**
 * Extracts nameWithOwner (owner/repo) from a milestone URL.
 * URL format: github.com/owner/repo/milestone/3 or https://github.com/owner/repo/milestone/3
 */
function extractNameWithOwner(url: string): string {
  const parts = url.split('/')
  const milestoneIndex = parts.lastIndexOf('milestone')
  return `${parts[milestoneIndex - 2]}/${parts[milestoneIndex - 1]}`
}

export function MilestoneRowMenu({milestone: data}: MilestoneRowMenuProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorsPresent, setErrorsPresent] = useState(false)

  const errorBanner = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const tanstackToggleEnabled = isFeatureEnabled('issues_milestone_toggle_tanstack')
  const tanstackDeleteEnabled = isFeatureEnabled('issues_milestone_delete_tanstack')

  const showToggleMilestoneErrorToast = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'error',
      message: 'Could not change Milestone state',
    })
  }, [addToast])

  const toggleMutation = useToggleMilestoneStateMutation({
    onError: showToggleMilestoneErrorToast,
  })

  const deleteMutation = useDeleteMilestoneMutation({
    onSuccess: () => {
      setIsDeleteDialogOpen(false)
      setIsProcessing(false)
      setErrorsPresent(false)
    },
    onError: () => {
      setErrorsPresent(true)
      setIsProcessing(false)
    },
  })

  const editCallback = useCallback(() => {
    const href = `${data.url.replace('/milestone/', '/milestones/')}/edit`
    const hrefParts = href.split('/')
    const milestoneIndex = hrefParts.lastIndexOf('milestone')
    if (milestoneIndex !== -1 && hrefParts[milestoneIndex + 1] === '') {
      hrefParts[milestoneIndex] = 'milestones'
    }
    const correctedHref = hrefParts.join('/')
    const url = new URL(correctedHref, window.location.origin)
    const path = url.pathname
    navigate(path)
  }, [data.url, navigate])

  const closeCallback = useCallback(() => {
    const lastState = data.state
    const newState = lastState === 'CLOSED' ? 'OPEN' : 'CLOSED'

    if (tanstackToggleEnabled) {
      toggleMutation.mutate({
        id: data.id,
        number: extractMilestoneNumber(data.url),
        nameWithOwner: extractNameWithOwner(data.url),
        state: newState,
      })
      return
    }

    commitUpdateMilestoneMutation({
      environment,
      input: {id: data.id, state: newState},
      onCompleted: () => {
        environment.commitUpdate(store => {
          const getOpenMilestones = store.get(`client:${data.repository.id}:milestones(first:0,states:"OPEN")`)
          if (getOpenMilestones) {
            const totalCount = getOpenMilestones.getValue('totalCount') as number
            getOpenMilestones.setValue(lastState === 'CLOSED' ? totalCount + 1 : totalCount - 1, 'totalCount')
          }
          const getClosedMilestones = store.get(`client:${data.repository.id}:milestones(first:0,states:"CLOSED")`)
          if (getClosedMilestones) {
            const totalCount = getClosedMilestones.getValue('totalCount') as number
            getClosedMilestones.setValue(lastState === 'CLOSED' ? totalCount - 1 : totalCount + 1, 'totalCount')
          }

          const searchString = searchParams.get('state')
          const state = searchString !== 'closed' ? 'OPEN' : 'CLOSED'

          const directionString = searchParams.get('direction')
          const direction = directionString === 'asc' ? 'ASC' : 'DESC'

          const orderString = searchParams.get('sort')
          let orderBy = 'CREATED_AT'
          if (orderString) {
            orderBy = orderString === 'due_date' ? 'DUE_DATE' : 'UPDATED_AT'
          }

          const connectionId = `client:${data.repository.id}:__MilestoneList_milestones_connection(orderBy:{"direction":"${direction}","field":"${orderBy}"},states:["${state}"])`
          const connectionRecord = store.get(connectionId)
          if (connectionRecord) {
            const edges = connectionRecord
              .getLinkedRecords('edges')
              ?.filter(edge => edge?.getLinkedRecord('node')?.getValue('id') !== data.id)
            connectionRecord.setLinkedRecords(edges || [], 'edges')
          }

          const newConnectionId = `client:${data.repository.id}:__MilestoneList_milestones_connection(orderBy:{"direction":"${direction}","field":"${orderBy}"},states:["${newState}"])`
          const newConnectionRecord = store.get(newConnectionId)

          if (newConnectionRecord) {
            const edges = newConnectionRecord.getLinkedRecords('edges') || []
            const milestoneNode = store.get(data.id)
            if (milestoneNode) {
              const edgeId = `client:newEdge:${data.id}`
              let newEdge = store.get(edgeId)
              if (!newEdge) {
                newEdge = store.create(edgeId, 'MilestoneEdge')
              }
              newEdge.setLinkedRecord(milestoneNode, 'node')
              newConnectionRecord.setLinkedRecords([newEdge, ...edges], 'edges')
            }
          }
        })
      },
      onError: showToggleMilestoneErrorToast,
    })
  }, [
    data.id,
    data.repository.id,
    data.state,
    data.url,
    environment,
    searchParams,
    showToggleMilestoneErrorToast,
    tanstackToggleEnabled,
    toggleMutation,
  ])

  const deleteCallback = useCallback(() => {
    if (isProcessing) return

    setIsProcessing(true)

    if (tanstackDeleteEnabled) {
      deleteMutation.mutate({
        number: extractMilestoneNumber(data.url),
        nameWithOwner: extractNameWithOwner(data.url),
      })
      return
    }

    // RELAY-CLEANUP: Remove this Relay path once issues_milestone_delete_tanstack is fully rolled out
    commitDeleteMilestoneMutation({
      environment,
      input: {id: data.id},
      onCompleted: () => {
        // Update the counters in the store
        environment.commitUpdate(store => {
          const searchString = searchParams.get('state')
          const state = searchString !== 'closed' ? 'OPEN' : 'CLOSED'
          const getOpenMilestones = store.get(`client:${data.repository.id}:milestones(first:0,states:"OPEN")`)
          if (getOpenMilestones && state === 'OPEN') {
            const totalCount = getOpenMilestones.getValue('totalCount') as number
            getOpenMilestones.setValue(Math.max(totalCount - 1, 0), 'totalCount')
          }
          const getClosedMilestones = store.get(`client:${data.repository.id}:milestones(first:0,states:"CLOSED")`)
          if (getClosedMilestones && state === 'CLOSED') {
            const totalCount = getClosedMilestones.getValue('totalCount') as number
            getClosedMilestones.setValue(Math.max(totalCount - 1, 0), 'totalCount')
          }
        })
        setIsDeleteDialogOpen(false)
        setIsProcessing(false)
        setErrorsPresent(false)
      },
      onError: () => {
        setErrorsPresent(true)
        setIsProcessing(false)
      },
    })
  }, [
    data.id,
    data.repository.id,
    data.url,
    environment,
    searchParams,
    isProcessing,
    tanstackDeleteEnabled,
    deleteMutation,
  ])

  // Prevent Enter action from bubbling up and redirecting to milestone show
  const handleMenuButtonKeyDown = useCallback((event: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Enter') {
      event.stopPropagation()
    }
  }, [])

  useEffect(() => {
    if (errorsPresent && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, errorsPresent])

  if (!data.repository.viewerCanPush) {
    return null
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button
          className={styles.menuButton}
          aria-label="Milestone menu"
          variant="invisible"
          icon={<KebabHorizontalIcon />}
          onKeyDown={handleMenuButtonKeyDown}
        />

        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.Item onSelect={() => editCallback()}>Edit</ActionList.Item>
            <ActionList.Item onSelect={() => closeCallback()}>
              {data.state === 'CLOSED' ? 'Open' : 'Close'}
            </ActionList.Item>
            <ActionList.Divider />
            <ActionList.Item onSelect={() => setIsDeleteDialogOpen(true)}>
              <span className={styles.deleteMilestoneButton}>Delete</span>
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      {isDeleteDialogOpen && (
        <ConfirmationDialog
          onClose={gesture => {
            if (gesture === 'confirm') {
              deleteCallback()
            } else {
              setIsDeleteDialogOpen(false)
              setErrorsPresent(false)
            }
          }}
          title={<p className={styles.dialogTitle}>{LABELS.deleteMilestoneConfirmationTitle}</p>}
          cancelButtonContent={LABELS.cancel}
          confirmButtonContent={LABELS.deleteMilestoneConfirmationButton}
          confirmButtonType="danger"
        >
          <div className={styles.dialogDescriptionContainer}>
            {errorsPresent && (
              <Banner
                ref={errorBanner}
                className="tmp-mb-3"
                title="Error"
                description={<AriaAlert>{LABELS.deleteMilestoneError}</AriaAlert>}
                variant="critical"
                role="alert"
              />
            )}
            <p className={styles.dialogDescription}>{LABELS.deleteMilestoneWarningPermanent}</p>
            <p className={styles.dialogDescription}>{LABELS.deleteMilestoneAssociatedIssuesNote}</p>
          </div>
        </ConfirmationDialog>
      )}
    </>
  )
}
